import { CrewCertificate } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';

/**
 * Certificates reference employees by documentId (relation) while older
 * records may hold the numeric id, so match on both.
 */
export const findEmployee = (employees: any[], employeeId?: string | number | null) => {
  if (employeeId === undefined || employeeId === null || employeeId === '') return undefined;
  const key = String(employeeId);
  return employees.find((e: any) => e.documentId === key || String(e.id) === key);
};

export const getEmployeeCertificates = (cert: CrewCertificate, certificates: CrewCertificate[]) =>
  certificates.filter(c => c.employeeId && String(c.employeeId) === String(cert.employeeId));

/**
 * Older document records were saved without expiryDate even though the
 * linked employee/car stores the matching expiry (card, seaman's book,
 * passport, license). Recover it from the owner when the document type
 * clearly identifies which document it is.
 */
const DOC_EXPIRY_RULES: { pattern: RegExp; ownerField: string }[] = [
  { pattern: /national\s*id|بطاق/i, ownerField: 'cardExpiryDate' },
  { pattern: /seaman|بحري/i, ownerField: 'seamanBookExpiry' },
  { pattern: /passport|جواز/i, ownerField: 'marinePassportExpiry' },
  { pattern: /license|رخصة|registration|تسجيل/i, ownerField: 'licenseExpiry' },
];

export const resolveDocumentExpiry = (
  documentType: string | undefined | null,
  doc: { expiryDate?: string | null },
  owner: any,
): string | null => {
  if (doc?.expiryDate) return doc.expiryDate;
  if (!owner || !documentType) return null;
  const rule = DOC_EXPIRY_RULES.find(r => r.pattern.test(documentType));
  return (rule && owner[rule.ownerField]) || null;
};

export const getCertificateExpiryStatus = (cert: CrewCertificate) => {
  if (!cert.expiryDate) return 'valid';
  const expiryDate = new Date(cert.expiryDate);
  const now = new Date();
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(now.getMonth() + 3);
  if (expiryDate < now) return 'expired';
  if (expiryDate <= threeMonthsFromNow) return 'expiring_soon';
  return 'valid';
};

export const getExpiryStats = (filteredCertificates: CrewCertificate[]) => {
  let expiredCount = 0;
  let expiringSoonCount = 0;
  filteredCertificates.forEach(cert => {
    const status = getCertificateExpiryStatus(cert);
    if (status === 'expired') expiredCount++;
    else if (status === 'expiring_soon') expiringSoonCount++;
  });
  return { expiredCount, expiringSoonCount, totalDocuments: filteredCertificates.length };
};

type EmployeeLookup = (employeeId: string) => string;

export const buildExportData = (
  filteredCertificates: CrewCertificate[],
  getEmployeeCode: EmployeeLookup,
  getEmployeeName: EmployeeLookup,
  getEmployeeRank: EmployeeLookup,
) => {
  return filteredCertificates.map(cert => ({
    'Employee Code': getEmployeeCode(cert.employeeId!),
    'Employee Name': getEmployeeName(cert.employeeId!),
    'Rank': getEmployeeRank(cert.employeeId!),
    'Document Type': cert.courseType,
    'Profession': cert.profession,
    'Issue Date': cert.issueDate,
    'Expiry Date': cert.expiryDate,
    'Certificate Number': cert.certificateNumber,
    'Capacity': cert.capacity,
    'Limitation': cert.limitation,
    'Status': cert.status,
    'Expiry Status': getCertificateExpiryStatus(cert),
  }));
};

export const handleExportDocuments = (
  filteredCertificates: CrewCertificate[],
  getEmployeeCode: EmployeeLookup,
  getEmployeeName: EmployeeLookup,
  getEmployeeRank: EmployeeLookup,
) => {
  const data = buildExportData(filteredCertificates, getEmployeeCode, getEmployeeName, getEmployeeRank);
  exportToExcel(data, `crew_and_cars_documents_${new Date().toISOString().split('T')[0]}`);
};

export const handlePrintDocuments = (
  filteredCertificates: CrewCertificate[],
  employees: any[],
  getEmployeeName: EmployeeLookup,
) => {
  const exportData = filteredCertificates.map(cert => {
    const employee = findEmployee(employees, cert.employeeId);
    const employeeDocs = getEmployeeCertificates(cert, filteredCertificates);
    return {
      'Employee Name': getEmployeeName(cert.employeeId!),
      'Employee Code': employee?.code || 'N/A',
      'Rank': employee?.rank || 'N/A',
      'Total Documents': employeeDocs.length,
      'Valid Documents': employeeDocs.filter(d => getCertificateExpiryStatus(d) === 'valid').length,
      'Expired Documents': employeeDocs.filter(d => getCertificateExpiryStatus(d) === 'expired').length,
      'Expiring Documents': employeeDocs.filter(d => getCertificateExpiryStatus(d) === 'expiring_soon').length,
    };
  });
  printAsExcel(exportData, 'Crew and Cars Documents');
};
